import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import proxy from "../../proxy";
import {
  Box,
  Container,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  HStack,
  Badge,
  useDisclosure,
  useToast,
  Text,
  VStack,
  Spinner,
  Center,
  Input,
  InputGroup,
  InputLeftElement,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  EditIcon,
  DeleteIcon,
  AddIcon,
  ArrowBackIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import StockModal from "../../components/Admin/StockModal";

const AdminStocks = () => {
  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStock, setSelectedStock] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  // Fetch stocks
  const fetchStocks = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${proxy}server_mts/admin/GetAllStocks.php`);
      setStocks(response.data);
      setFilteredStocks(response.data);
    } catch (error) {
      toast({
        title: "Error loading stocks",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredStocks(stocks);
    } else {
      const filtered = stocks.filter(
        (s) =>
          s.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.ticker.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredStocks(filtered);
    }
  }, [searchQuery, stocks]);

  // Add stock
  const handleAdd = () => {
    setSelectedStock(null);
    onOpen();
  };

  // Edit stock
  const handleEdit = (stock) => {
    setSelectedStock(stock);
    onOpen();
  };

  // Delete stock
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this stock?")) {
      return;
    }

    try {
      await axios.post(`${proxy}server_mts/admin/DeleteStock.php`, { id });
      toast({
        title: "Stock deleted successfully",
        status: "success",
        duration: 2000,
        position: "top",
      });
      fetchStocks();
    } catch (error) {
      toast({
        title: "Error deleting stock",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    }
  };

  // Save stock
  const handleSave = () => {
    onClose();
    setTimeout(() => {
      fetchStocks();
    }, 200);
  };

  // Calculate stats
  const totalShares = stocks.reduce((sum, s) => sum + Number(s.nr_shares), 0);
  const totalInvested = stocks.reduce(
    (sum, s) => sum + Number(s.nr_shares) * Number(s.average_price_share),
    0
  );
  const totalDividendsPaid = stocks.reduce((sum, s) => sum + Number(s.paid_div), 0);
  const totalCommissions = stocks.reduce((sum, s) => sum + Number(s.commission), 0);

  if (isLoading) {
    return (
      <Box minH="100vh" bg="gray.900">
        <Center minH="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="teal.400" thickness="4px" />
            <Text color="gray.400">Loading stocks...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.900">
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.95))"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        py={4}
        px={6}
        boxShadow="0 4px 20px rgba(0,0,0,0.3)"
      >
        <Container maxW="1400px">
          <HStack justify="space-between">
            <HStack spacing={4}>
              <IconButton
                icon={<ArrowBackIcon />}
                variant="ghost"
                colorScheme="teal"
                onClick={() => navigate("/admin/dashboard")}
                aria-label="Back to dashboard"
              />
              <VStack align="start" spacing={0}>
                <Heading
                  size="md"
                  bgGradient="linear(to-r, teal.300, cyan.400)"
                  bgClip="text"
                >
                  📈 Stock Portfolio Management
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Manage dividend stocks and track performance
                </Text>
              </VStack>
            </HStack>

            <Button
              leftIcon={<AddIcon />}
              colorScheme="teal"
              onClick={handleAdd}
              size="sm"
            >
              Add Stock
            </Button>
          </HStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="1400px" py={6}>
        <VStack spacing={6} align="stretch">
          {/* Stats Overview */}
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4}>
            <Box
              p={4}
              bg="rgba(26,32,44,0.6)"
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Total Stocks
                </StatLabel>
                <StatNumber color="teal.300" fontSize="2xl">
                  {stocks.length}
                </StatNumber>
              </Stat>
            </Box>

            <Box
              p={4}
              bg="rgba(26,32,44,0.6)"
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Total Shares
                </StatLabel>
                <StatNumber color="blue.300" fontSize="2xl">
                  {totalShares.toFixed(2)}
                </StatNumber>
              </Stat>
            </Box>

            <Box
              p={4}
              bg="rgba(26,32,44,0.6)"
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Total Invested
                </StatLabel>
                <StatNumber color="purple.300" fontSize="2xl">
                  ${totalInvested.toFixed(2)}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="2xs">
                  + ${totalCommissions.toFixed(2)} fees
                </StatHelpText>
              </Stat>
            </Box>

            <Box
              p={4}
              bg="rgba(26,32,44,0.6)"
              borderRadius="lg"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Dividends Paid
                </StatLabel>
                <StatNumber color="green.300" fontSize="2xl">
                  ${totalDividendsPaid.toFixed(2)}
                </StatNumber>
              </Stat>
            </Box>
          </SimpleGrid>

          {/* Search */}
          <HStack justify="space-between" spacing={4}>
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search by company or ticker..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
                color="white"
                size="sm"
              />
            </InputGroup>

            <Badge colorScheme="teal" fontSize="sm" px={3} py={1}>
              {filteredStocks.length} stock{filteredStocks.length !== 1 ? "s" : ""}
            </Badge>
          </HStack>

          {/* Table */}
          <Box
            bg="linear-gradient(135deg, rgba(26,32,44,0.97), rgba(45,55,72,0.97))"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.200"
            overflow="hidden"
            boxShadow="0 10px 30px rgba(0,0,0,0.3)"
          >
            {filteredStocks.length === 0 ? (
              <Center py={12}>
                <VStack spacing={3}>
                  <Text fontSize="4xl">📭</Text>
                  <Text color="gray.400" fontSize="lg">
                    {searchQuery ? "No stocks found" : "No stocks yet"}
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {searchQuery
                      ? "Try a different search term"
                      : "Click 'Add Stock' to get started"}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="whiteAlpha.50">
                    <Tr>
                      <Th color="teal.300" borderColor="whiteAlpha.200">Ticker</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200">Company</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" isNumeric>Shares</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" isNumeric>Avg Price</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" isNumeric>Div/Share</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" isNumeric>Div/Year</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" isNumeric>Paid Div</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" isNumeric>Commission</Th>
                      <Th color="teal.300" borderColor="whiteAlpha.200" textAlign="right">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredStocks.map((stock) => (
                      <Tr
                        key={stock.id}
                        _hover={{ bg: "whiteAlpha.50" }}
                        transition="0.2s"
                      >
                        <Td borderColor="whiteAlpha.100">
                          <Badge colorScheme="purple" fontSize="xs" fontWeight="bold">
                            {stock.ticker}
                          </Badge>
                        </Td>
                        <Td color="white" borderColor="whiteAlpha.100">
                          {stock.company}
                        </Td>
                        <Td color="blue.300" fontWeight="bold" borderColor="whiteAlpha.100" isNumeric>
                          {Number(stock.nr_shares).toFixed(2)}
                        </Td>
                        <Td color="gray.300" borderColor="whiteAlpha.100" isNumeric>
                          ${Number(stock.average_price_share).toFixed(2)}
                        </Td>
                        <Td color="green.300" fontWeight="bold" borderColor="whiteAlpha.100" isNumeric>
                          ${Number(stock.div_share).toFixed(4)}
                        </Td>
                        <Td color="yellow.300" borderColor="whiteAlpha.100" isNumeric>
                          {stock.div_per_year}x
                        </Td>
                        <Td color="green.400" fontWeight="bold" borderColor="whiteAlpha.100" isNumeric>
                          ${Number(stock.paid_div).toFixed(2)}
                        </Td>
                        <Td color="red.300" borderColor="whiteAlpha.100" isNumeric>
                          ${Number(stock.commission).toFixed(2)}
                        </Td>
                        <Td borderColor="whiteAlpha.100" textAlign="right">
                          <HStack spacing={2} justify="flex-end">
                            <IconButton
                              icon={<EditIcon />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => handleEdit(stock)}
                              aria-label="Edit stock"
                            />
                            <IconButton
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDelete(stock.id)}
                              aria-label="Delete stock"
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </VStack>
      </Container>

      {/* Modal */}
      <StockModal
        isOpen={isOpen}
        onClose={onClose}
        stock={selectedStock}
        onSave={handleSave}
      />
    </Box>
  );
};

export default AdminStocks;